// Published-content extras for one department page — deliberately narrow,
// the same discipline as publishedFacultyPhotos.ts/mergedFaculty.ts.
//
// tagline, about (summary+overview), vision, mission, peos and psos are the
// *same* source departments.ts already hand-carries — checked CS
// byte-for-byte, identical — so departments.ts is duplicated content, not an
// independent source. Published wins here (falling back to departments.ts
// only where a department isn't published yet, e.g. the four basic-science
// departments, or a field is empty on the published record) so this content
// lives in exactly one place going forward instead of two.
//
// stats and hod message/photo/cover are genuinely additive, not duplicates:
//
//   stats        facultyCount / laboratoryCount / sanctionedIntake — only
//                3 of 19 departments have this populated (2026-09-17), but
//                where it exists it's real and we don't store it elsewhere.
//
//   hod message  most departments' published HOD message is *also* the same
//                text departments.ts already has (byte-identical for CS,
//                CB, CG, CH, ME, STR, TX) — using it as a blind override
//                would replace a well-formatted multi-paragraph bio with a
//                single-block version of the exact same words for several
//                departments (EC, EE, BT, CD, ENV — all within 1 character
//                of their local length, clearly the same text reflowed).
//                So this only wins when it's MEANINGFULLY longer than what
//                departments.ts already has (a 50-char margin — comfortably
//                above every "same text, different formatting" case above,
//                comfortably below MCA's real +457-char improvement) — a
//                genuine gap being filled, not noise.
//
//   hod photo / cover  a department's HOD portrait and banner are each a
//                single, unambiguous photo per department — unlike the
//                general `images[]` array (see below), there's no room-label
//                parsing problem here, so scripts/process-content-images.mjs
//                reprocesses these two specifically (sites/biet/site-content/
//                departments/<slug>-hod.<ext> and <slug>-cover.<ext>) the
//                same way it already does faculty.photo.
//
// Explicitly NOT wired here, and why:
//   - AI's HOD message is still truncated mid-sentence as of 2026-09-17
//     (one holdout after an earlier publish fixed the other 15) — the
//     "clean sentence" check below excludes it the same way it would any
//     future truncation, without needing a per-department exception.
//   - highlights (a real per-department events feed) has nowhere to go:
//     departments.ts's own `activities` field exists but its section was
//     deliberately hidden "by request" (see Department.tsx) — reusing or
//     un-hiding that space isn't this file's call to make.
//   - the general images[] array: published photos there carry no
//     structured room/type data (free-text alt only, e.g. "Classroom
//     A321"), unlike labPhotos' `space`/`label` fields — parsing that
//     reliably is unsolved. hod.photo and cover are each exactly one photo
//     with one unambiguous role, which is what makes those two safe.
//   - notableAlumni is handled separately, in publishedAlumni.ts: it's a
//     list of real people rather than one text field, and where published
//     it replaces the department's own list as a whole.
//
// establishedYear (by request): genuinely additive where `about` is empty —
// e.g. MAD (established 2025) has no local prose at all, so this is the
// only place that fact exists. Shown even for departments where `about`
// already states it in prose (e.g. CS, "established in 1984") since there's
// no other structured field for it to conflict with.
import type { Media } from "@zivin-labs/site-content";
import { useCollection } from "./siteContent";
import { resolveDepartment, type Department } from "./departments";

export interface PublishedDepartmentStats {
  asOfAcademicYear?: string;
  facultyCount?: number;
  laboratoryCount?: number;
  sanctionedIntakeUg?: number;
  sanctionedIntakePg?: number;
}

export interface PublishedDepartmentExtras {
  /** Null unless published content offers a real, complete improvement over what's already shown. */
  hodMessage: string | null;
  stats: PublishedDepartmentStats | null;
  /** Null unless process-content-images.mjs has reprocessed one (bare key, ready for <Figure>). */
  hodPhoto: Media | null;
  coverPhoto: Media | null;
  /** The rest are plain duplicates of departments.ts — null only when the published record has nothing there, so the caller falls back to departments.ts. */
  tagline: string | null;
  about: string[] | null;
  vision: string | null;
  mission: string[] | null;
  peos: string[] | null;
  psos: string[] | null;
  /**
   * The published record's OWN code, e.g. "CSE" — webadmin's legacy 3-letter
   * spelling, as opposed to departments.ts's `code` ("CS"). Not a duplicate:
   * only 4 departments (CS/CSE, IS/ISE, EC/ECE, EE/EEE) actually differ —
   * everywhere else the two codes are already identical, so the caller
   * should only show this when it differs from departments.ts's own code.
   */
  publishedCode: string | null;
  establishedYear: number | null;
}

interface PublishedMediaRef {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
}

interface PublishedOutcome {
  code: string;
  statement: string;
  kind?: "peo" | "po" | "pso";
}

interface PublishedDeptRecord {
  code: string;
  hod: { message?: string | null; photo?: PublishedMediaRef | null } | null;
  cover: PublishedMediaRef | null;
  stats: PublishedDepartmentStats | null;
  establishedYear?: number | null;
  tagline?: string | null;
  summary?: string | null;
  overview?: string | null;
  vision?: string | null;
  mission?: string[] | null;
  outcomes?: PublishedOutcome[] | null;
}

const EMPTY: PublishedDepartmentExtras = {
  hodMessage: null,
  stats: null,
  hodPhoto: null,
  coverPhoto: null,
  tagline: null,
  about: null,
  vision: null,
  mission: null,
  peos: null,
  psos: null,
  publishedCode: null,
  establishedYear: null,
};

/** Outcomes of one kind, in the order the record's own `code` sorts (webadmin doesn't guarantee array order). */
function outcomesOfKind(outcomes: PublishedOutcome[], kind: "peo" | "pso"): string[] | null {
  const matched = outcomes
    .filter((o) => o.kind === kind && o.statement?.trim())
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  return matched.length > 0 ? matched.map((o) => o.statement.trim()) : null;
}

/**
 * A reprocessed photo is a bare key (no "://") — Figure.tsx's own
 * isBareKey check for what it's willing to route through the transform CDN.
 * A still-raw webadmin URL isn't used: better no override than one that
 * bypasses the responsive/WebP pipeline every other image on the site gets.
 */
function asMedia(ref: PublishedMediaRef | null | undefined, fallbackAlt: string): Media | null {
  if (!ref?.url || ref.url.includes("://") || !ref.width || !ref.height) return null;
  return { url: ref.url, alt: ref.alt || fallbackAlt, width: ref.width, height: ref.height };
}

/** A message that doesn't end mid-word/mid-sentence — excludes the known truncation case without a per-department list. */
function isCleanSentence(text: string): boolean {
  return /[.!?]["'’”)\]]?\s*$/.test(text.trim());
}

/**
 * @param code This department's own identity code — d.code, e.g. "CS" or
 *   "STR" — NEVER d.staffCode. staffCode groups departments that share one
 *   faculty pool in staff.ts (env, str and cv all carry staffCode "CV",
 *   since their faculty are all listed under CV) — reusing it here to
 *   identify WHICH published department record belongs to this page matched
 *   all three of them to whichever of the three appears first in the
 *   published collection, silently swapping in another department's HOD
 *   photo. `code` is the published record's own code too, just spelled with
 *   the legacy 3-letter forms (CSE/ECE/EEE/ISE) resolveDepartment() already
 *   knows how to normalize — it is never shared between two departments.
 * @param currentBioLength Length of the bio already on the page, so the
 *   "meaningfully longer" comparison happens against what's actually there.
 */
export function usePublishedDepartmentExtras(
  code: string | undefined,
  currentBioLength: number,
): PublishedDepartmentExtras {
  const { items } = useCollection("departments");
  if (!code) return EMPTY;

  const records = items as unknown as PublishedDeptRecord[];
  const rec = records.find((r) => {
    const resolved = resolveDepartment(r.code);
    const resolvedCode = (resolved?.code ?? "").toUpperCase();
    return resolvedCode === code.toUpperCase();
  });
  if (!rec) return EMPTY;

  const msg = rec.hod?.message?.trim() || "";
  const hodMessage = msg && isCleanSentence(msg) && msg.length > currentBioLength + 50 ? msg : null;

  const about = [rec.summary?.trim(), rec.overview?.trim()].filter((p): p is string => Boolean(p));
  const mission = (rec.mission ?? []).map((m) => m.trim()).filter(Boolean);
  const outcomes = rec.outcomes ?? [];

  return {
    hodMessage,
    stats: rec.stats ?? null,
    hodPhoto: asMedia(rec.hod?.photo, `${rec.code} HOD`),
    coverPhoto: asMedia(rec.cover, `${rec.code} department`),
    tagline: rec.tagline?.trim() || null,
    about: about.length > 0 ? about : null,
    vision: rec.vision?.trim() || null,
    mission: mission.length > 0 ? mission : null,
    peos: outcomesOfKind(outcomes, "peo"),
    psos: outcomesOfKind(outcomes, "pso"),
    publishedCode: rec.code?.trim() || null,
    establishedYear: rec.establishedYear ?? null,
  };
}

/**
 * The line a department's card carries on a listing page (Academics): the
 * department's published tagline, exactly as published, or departments.ts's
 * own `intro` where none is published — the same choice the department's own
 * page makes for its lead (Department.tsx). Matched on the published record's
 * code resolving to this department, never on a name.
 */
export function useDepartmentTaglines(): (d: Department) => string {
  const records = useCollection("departments").items as unknown as PublishedDeptRecord[];
  return (d) => {
    const rec = records.find((r) => resolveDepartment(r.code)?.code === d.code);
    return rec?.tagline?.trim() || d.intro;
  };
}

/**
 * A department's published laboratory count, or null where none is published —
 * never a count of the local `labs` table. The listing cards and the department
 * page's "Published laboratories" tile both use this, so they can't disagree.
 */
export function useDepartmentLabCounts(): (d: Department) => number | null {
  const records = useCollection("departments").items as unknown as PublishedDeptRecord[];
  return (d) => {
    const rec = records.find((r) => resolveDepartment(r.code)?.code === d.code);
    return rec?.stats?.laboratoryCount ?? null;
  };
}
